import { MISSING_AUTH_TOKEN_CODE, readAccessToken } from '@/lib/api/auth-token'
import { ApiError } from '@/lib/api/errors'

import { fetchCsChatSession } from './api'
import { getOrCreateDeviceId } from './device-id'
import { initAndOpenYellowWidget, openYellowBot } from './yellow-widget'

interface CachedCsSession {
  accessToken: string
  initialized: boolean
}

let cachedSession: CachedCsSession | null = null
let inFlightLaunchPromise: Promise<void> | null = null
let sessionVersion = 0

/** Call after persisting the new auth state, so a reload restores that state. */
export function resetLionaChat(): void {
  sessionVersion += 1
  cachedSession = null
  inFlightLaunchPromise = null

  // Yellow.ai v2 has no destroy API. A reload disposes its iframe, connections,
  // and anonymous page listeners, including a script that is still loading.
  if (typeof window !== 'undefined' && (window.YellowMessengerPlugin || window.YellowMessenger)) {
    window.location.reload()
  }
}

/**
 * Checks if an error is an authentication error (401 or missing session token)
 * which already handles redirection to login and should not show the error alert.
 */
export function isCsChatAuthError(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.status === 401 || error.code === MISSING_AUTH_TOKEN_CODE
  }
  return false
}

/**
 * Opens the Yellow.ai customer support chat panel for authenticated users.
 * - Deduplicates concurrent in-flight launch calls.
 * - Reuses existing initialized session for subsequent clicks without re-minting.
 * - Throws any network, timeout, or API errors for caller to display alert.
 */
export async function openLionaChat(): Promise<void> {
  if (inFlightLaunchPromise) {
    return inFlightLaunchPromise
  }

  const launchVersion = sessionVersion
  const launchPromise = (async () => {
    const currentToken = readAccessToken()

    // Reuse session and open panel directly if already initialized for the current auth token
    if (
      cachedSession &&
      currentToken &&
      cachedSession.accessToken === currentToken &&
      cachedSession.initialized &&
      typeof window !== 'undefined' &&
      window.YellowMessengerPlugin
    ) {
      openYellowBot()
      return
    }

    const deviceId = getOrCreateDeviceId()
    const session = await fetchCsChatSession(deviceId)
    if (launchVersion !== sessionVersion) return

    await initAndOpenYellowWidget(session)
    if (launchVersion !== sessionVersion) return

    cachedSession = {
      accessToken: currentToken ?? '',
      initialized: true,
    }
  })()
  inFlightLaunchPromise = launchPromise

  try {
    await launchPromise
  } finally {
    if (inFlightLaunchPromise === launchPromise) inFlightLaunchPromise = null
  }
}
